import { useCallback, useEffect, useRef, useState } from 'react';
import { sidebarConfig, useSidebarOpenState } from '@backstage/core-components';
import type { IconComponent } from '@backstage/core-plugin-api';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import { fade, makeStyles, useTheme } from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Link, useLocation } from 'react-router-dom';

export type FloatingSidebarItem = {
  title: string;
  description: string;
  icon: IconComponent;
  to: string;
  activePaths?: readonly string[];
};

type Props = {
  id: string;
  title: string;
  icon: IconComponent;
  items: readonly FloatingSidebarItem[];
  activePaths?: readonly string[];
};

export const FloatingSidebarMenu = ({
  id,
  title,
  icon: Icon,
  items,
  activePaths,
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const { isOpen } = useSidebarOpenState();
  const { pathname } = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [focusMenu, setFocusMenu] = useState(false);
  const open = anchorEl !== null;
  const matchesPath = (path: string) =>
    pathname === path || pathname.startsWith(`${path}/`);
  const isCurrent = (activePaths ?? items.map(item => item.to)).some(
    matchesPath,
  );
  const triggerId = `${id}-menu-trigger`;
  const menuId = `${id}-menu`;
  const closeMenu = useCallback((restoreFocus = false) => {
    setAnchorEl(null);
    setFocusMenu(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);
  const openMenu = (focus = false) => {
    setAnchorEl(triggerRef.current);
    setFocusMenu(focus);
    if (focus && open) {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  };
  const handleMouseLeave = () => {
    if (menuRef.current?.contains(document.activeElement)) return;
    closeMenu();
  };

  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  return (
    <ClickAwayListener onClickAway={() => closeMenu()}>
      <div
        className={classes.root}
        onMouseEnter={() => {
          if (!open) openMenu();
        }}
        onMouseLeave={handleMouseLeave}
        onBlur={event => {
          const target = event.relatedTarget;
          if (
            !(target instanceof Node) ||
            (!triggerRef.current?.contains(target) &&
              !menuRef.current?.contains(target))
          ) {
            closeMenu();
          }
        }}
        onKeyDown={event => {
          if (open && (event.key === 'Escape' || event.key === 'ArrowLeft')) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu(true);
          }
        }}
      >
        <ButtonBase
          ref={triggerRef}
          id={triggerId}
          type="button"
          aria-label={title}
          aria-haspopup="menu"
          aria-controls={open ? menuId : undefined}
          aria-expanded={open}
          aria-current={isCurrent ? 'true' : undefined}
          className={`${classes.trigger} ${isCurrent ? classes.current : ''}`}
          focusVisibleClassName={classes.focusVisible}
          onClick={() => openMenu(true)}
          onKeyDown={event => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              event.preventDefault();
              openMenu(true);
            }
          }}
        >
          <Box component="span" className={classes.triggerIcon}>
            <Icon fontSize="small" />
          </Box>
          {isOpen && (
            <>
              <Typography
                component="span"
                variant="subtitle2"
                className={classes.label}
              >
                {title}
              </Typography>
              <ChevronRightIcon className={classes.chevron} fontSize="small" />
            </>
          )}
        </ButtonBase>
        <Popper
          role="presentation"
          className={classes.flyout}
          style={{ zIndex: theme.zIndex.snackbar + 1 }}
          anchorEl={anchorEl}
          open={open}
          placement="right-start"
          modifiers={{
            preventOverflow: {
              boundariesElement: 'viewport',
              padding: theme.spacing(2),
            },
            flip: {
              boundariesElement: 'viewport',
              padding: theme.spacing(2),
            },
          }}
        >
          <Paper className={classes.paper} elevation={8}>
            <MenuList
              ref={menuRef}
              id={menuId}
              aria-labelledby={triggerId}
              className={classes.menuList}
              variant="menu"
              autoFocusItem={focusMenu}
              onKeyDown={event => {
                if (event.key === 'Tab') {
                  event.preventDefault();
                  closeMenu(true);
                }
              }}
            >
              {items.map(item => {
                const selected = (item.activePaths ?? [item.to]).some(
                  matchesPath,
                );
                return (
                  <MenuItem
                    key={item.to}
                    component={Link}
                    to={item.to}
                    selected={selected}
                    onClick={() => closeMenu(true)}
                    className={classes.item}
                  >
                    <ListItemIcon
                      className={`${classes.icon} ${
                        selected ? classes.currentIcon : ''
                      }`}
                    >
                      <item.icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={item.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        className: classes.itemTitle,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        className: classes.description,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  trigger: {
    color: theme.palette.navigation.color,
    height: theme.spacing(6),
    justifyContent: 'flex-start',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.navigation.navItem?.hoverBackground,
    },
  },
  current: {
    boxShadow: `inset ${sidebarConfig.selectedIndicatorWidth}px 0 0 ${theme.palette.navigation.indicator}`,
    color: theme.palette.navigation.selectedColor,
  },
  focusVisible: {
    outline: `2px solid ${theme.palette.navigation.selectedColor}`,
    outlineOffset: -theme.spacing(0.5),
  },
  triggerIcon: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: '100%',
    justifyContent: 'center',
    marginRight: -theme.spacing(2),
    width: sidebarConfig.iconContainerWidth,
  },
  label: {
    flex: 1,
    fontWeight: 'bold',
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chevron: {
    flexShrink: 0,
    marginRight: theme.spacing(2),
  },
  flyout: {
    padding: theme.spacing(0, 1),
  },
  paper: {
    backgroundColor: theme.palette.navigation.submenu?.background,
    border: `1px solid ${fade(theme.palette.navigation.color, 0.24)}`,
    borderRadius: theme.spacing(1),
    color: theme.palette.navigation.selectedColor,
    maxHeight: `calc(100vh - ${theme.spacing(4)}px)`,
    maxWidth: `calc(100vw - ${theme.spacing(6)}px)`,
    overflowX: 'hidden',
    overflowY: 'auto',
    width: sidebarConfig.drawerWidthOpen,
  },
  menuList: {
    padding: theme.spacing(1),
  },
  item: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    whiteSpace: 'normal',
    '&:hover, &:focus, &.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: theme.palette.navigation.navItem?.hoverBackground,
    },
  },
  icon: {
    color: theme.palette.navigation.color,
    minWidth: theme.spacing(4),
  },
  currentIcon: {
    color: theme.palette.navigation.indicator,
  },
  itemTitle: {
    fontWeight: 'bold',
  },
  description: {
    color: theme.palette.navigation.color,
    display: 'block',
    lineHeight: theme.typography.body2.lineHeight,
  },
}));
